import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const NAV_LINKS = [
  { to: '/features', label: 'Features' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/faq', label: 'FAQ' },
];

const FEATURES_DEEP = [
  {
    icon: '⚡',
    color: '#3B82F6',
    title: 'Instant Autofill',
    tagline: 'Fill any carrier form in under 3 seconds.',
    bullets: [
      'Detects form fields automatically on 40+ carrier portals',
      'Fills name, DOB, address, vehicle, prior coverage and more',
      'Works on EZLynx, Applied Epic, Hawksoft, QQ Catalyst & direct portals',
      'Zero copy-paste errors — data flows straight from your CRM',
      'One-click override to manually edit any pre-filled field',
    ],
  },
  {
    icon: '🧠',
    color: '#8B5CF6',
    title: 'Smart Client CRM',
    tagline: 'Every client detail, one searchable place.',
    bullets: [
      'Store unlimited drivers, vehicles, properties and dependants',
      'Full prior coverage history with carrier and policy number',
      'Smart search — find any client in under 2 seconds',
      'Custom fields for your agency\'s unique workflow',
      'Activity log tracks every quote, note and change',
    ],
  },
  {
    icon: '🔄',
    color: '#06B6D4',
    title: 'Multi-Carrier Sync',
    tagline: 'One record. Infinite quotes.',
    bullets: [
      'Quote across 40+ carriers from a single client record',
      'All quoting sessions timestamped and saved automatically',
      'Side-by-side quote comparison dashboard',
      'Carrier portal credentials stored securely per agent',
      'New carrier integrations added every week',
    ],
  },
  {
    icon: '📊',
    color: '#10B981',
    title: 'Pipeline Dashboard',
    tagline: 'Your entire book of business at a glance.',
    bullets: [
      'Drag-and-drop Kanban pipeline: Lead → Quoted → Sold → Active',
      'Revenue forecasting based on open quotes',
      'Filter by agent, line of business, or carrier',
      'Daily summary email with pipeline snapshot',
      'Export pipeline to CSV or connect to your BI tool',
    ],
  },
  {
    icon: '🔔',
    color: '#F59E0B',
    title: 'Renewal Alerts',
    tagline: 'Never miss a renewal window again.',
    bullets: [
      'Automatic alerts at 90, 60, and 30 days before renewal',
      'Email and in-app notifications per agent',
      'One-click to open the client and start re-quoting',
      'Renewal calendar view across your entire book',
      'Track renewal outcomes — retained, lapsed, or moved carrier',
    ],
  },
  {
    icon: '📱',
    color: '#EF4444',
    title: 'Mobile App',
    tagline: 'Your CRM in your pocket.',
    bullets: [
      'Full client lookup and note-taking on iOS and Android',
      'Push notifications for renewal alerts and pipeline updates',
      'Offline mode — access client data without internet',
      'Biometric login for fast, secure access between appointments',
      'Sync instantly with desktop when back online',
    ],
  },
  {
    icon: '👥',
    color: '#EC4899',
    title: 'Team Collaboration',
    tagline: 'Built for agencies, not just solo agents.',
    bullets: [
      'Invite unlimited agents on Agency plan',
      'Manager-level permissions and read-only roles',
      'Shared client records with ownership assignment',
      'Team activity feed — see what everyone is working on',
      'Agency-wide reporting and leaderboard',
    ],
  },
  {
    icon: '🔌',
    color: '#6366F1',
    title: 'Integrations',
    tagline: 'Connect the tools you already use.',
    bullets: [
      'CSV import from any existing CRM',
      'Native sync with Salesforce, HubSpot and AgencyZoom',
      'Zapier integration for 5,000+ connected apps',
      'Open API for custom builds',
      'Chrome Extension works on any quoting platform',
    ],
  },
];

function SharedNav({ mobileOpen, setMobileOpen }) {
  return (
    <>
      <style>{`
        .nav{position:sticky;top:0;z-index:100;background:rgba(255,255,255,0.92);backdrop-filter:blur(16px);border-bottom:1px solid #E2E8F0;padding:0 2rem;height:68px;display:flex;align-items:center;justify-content:space-between;}
        .nav-logo{font-family:'Sora',sans-serif;font-size:1.25rem;font-weight:800;color:#2563EB;letter-spacing:-0.5px;text-decoration:none;display:flex;align-items:center;gap:0.4rem;}
        .nav-links a{color:#475569;font-size:0.9rem;font-weight:500;text-decoration:none;margin-left:2rem;transition:color 0.2s;}
        .nav-links a:hover,.nav-links a.active{color:#2563EB;}
        .btn-nav{background:#2563EB;color:#fff;padding:0.55rem 1.4rem;border-radius:8px;font-size:0.9rem;font-weight:600;text-decoration:none;transition:background 0.2s,transform 0.15s;border:none;cursor:pointer;}
        .btn-nav:hover{background:#1D4ED8;transform:translateY(-1px);}
        .hamburger{display:none;background:none;border:1px solid #E2E8F0;border-radius:8px;padding:0.4rem 0.6rem;cursor:pointer;font-size:1.2rem;}
        @media(max-width:768px){
          .nav-links,.nav-desktop-cta{display:none !important;}
          .hamburger{display:block;}
          .mobile-menu{position:fixed;top:68px;left:0;right:0;background:#fff;border-bottom:1px solid #E2E8F0;padding:1.5rem 2rem;z-index:99;box-shadow:0 8px 32px rgba(0,0,0,0.08);}
          .mobile-menu a{display:block;padding:0.75rem 0;color:#0F172A;font-size:1rem;font-weight:500;text-decoration:none;border-bottom:1px solid #f1f5f9;}
          .mobile-menu a:last-child{border-bottom:none;}
        }
      `}</style>
      <nav className="nav">
        <Link to="/" className="nav-logo">⚡ Prorately</Link>
        <div className="nav-links" style={{ display: 'flex', alignItems: 'center' }}>
          {NAV_LINKS.map(l => <Link key={l.to} to={l.to} className="nav-link">{l.label}</Link>)}
        </div>
        <div className="nav-desktop-cta" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Link to="/login" style={{ color: '#475569', fontWeight: 500, textDecoration: 'none', fontSize: '0.9rem' }}>Login</Link>
          <Link to="/register" className="btn-nav">Start Free Trial</Link>
        </div>
        <button className="hamburger" onClick={() => setMobileOpen(!mobileOpen)}>☰</button>
      </nav>
      {mobileOpen && (
        <div className="mobile-menu">
          {NAV_LINKS.map(l => <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}>{l.label}</Link>)}
          <Link to="/login" onClick={() => setMobileOpen(false)}>Login</Link>
          <Link to="/register" onClick={() => setMobileOpen(false)} style={{ color: '#2563EB', fontWeight: 700 }}>Start Free Trial →</Link>
        </div>
      )}
    </>
  );
}

export default function Features() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: '#F8FAFF', color: '#0F172A', overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Sora:wght@400;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        html{scroll-behavior:smooth;}
        .sora{font-family:'Sora',sans-serif;}

        .page-hero{background:linear-gradient(135deg,#1E3A8A 0%,#2563EB 60%,#3B82F6 100%);padding:5rem 2rem;text-align:center;position:relative;overflow:hidden;}
        .page-hero::before{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(255,255,255,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.05) 1px,transparent 1px);background-size:40px 40px;}
        .page-hero-content{position:relative;z-index:1;max-width:700px;margin:0 auto;}
        .page-tag{display:inline-block;background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.3);color:#fff;font-size:0.75rem;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;padding:0.35rem 0.9rem;border-radius:100px;margin-bottom:1.25rem;}
        .page-hero h1{font-family:'Sora',sans-serif;font-size:clamp(2rem,4vw,3.2rem);font-weight:800;color:#fff;line-height:1.15;letter-spacing:-0.03em;margin-bottom:1rem;}
        .page-hero p{color:rgba(255,255,255,0.8);font-size:1.1rem;line-height:1.7;max-width:560px;margin:0 auto 2rem;}
        .btn-white{background:#fff;color:#2563EB;padding:0.85rem 2rem;border-radius:12px;font-weight:700;font-size:0.95rem;text-decoration:none;border:none;cursor:pointer;transition:all 0.2s;display:inline-flex;align-items:center;gap:0.5rem;}
        .btn-white:hover{background:#EFF6FF;transform:translateY(-1px);box-shadow:0 8px 24px rgba(0,0,0,0.12);}

        .features-grid{max-width:1100px;margin:0 auto;padding:5rem 2rem;display:grid;grid-template-columns:repeat(auto-fit,minmax(480px,1fr));gap:2rem;}
        @media(max-width:600px){.features-grid{grid-template-columns:1fr;padding:3rem 1.25rem;}}

        .feat-card{background:#fff;border:1.5px solid #E2E8F0;border-radius:20px;padding:2.25rem;transition:all 0.25s;}
        .feat-card:hover{box-shadow:0 12px 40px rgba(37,99,235,0.1);transform:translateY(-3px);border-color:#93C5FD;}
        .feat-card-top{display:flex;align-items:flex-start;gap:1rem;margin-bottom:1.25rem;}
        .feat-icon-wrap{width:54px;height:54px;border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:1.6rem;flex-shrink:0;}
        .feat-card-title{font-family:'Sora',sans-serif;font-weight:800;font-size:1.15rem;color:#0F172A;margin-bottom:0.2rem;}
        .feat-card-tagline{font-size:0.88rem;color:#64748B;}
        .feat-bullets{list-style:none;display:flex;flex-direction:column;gap:0.6rem;}
        .feat-bullets li{display:flex;align-items:flex-start;gap:0.6rem;font-size:0.9rem;color:#475569;line-height:1.5;}
        .feat-bullet-dot{width:18px;height:18px;border-radius:50%;background:#EFF6FF;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px;}
        .feat-bullet-check{color:#2563EB;font-size:0.65rem;font-weight:900;}

        .features-cta{background:#0F172A;padding:5rem 2rem;text-align:center;}
        .features-cta h2{font-family:'Sora',sans-serif;font-size:clamp(1.8rem,3.5vw,2.5rem);font-weight:800;color:#fff;margin-bottom:1rem;letter-spacing:-0.02em;}
        .features-cta p{color:#64748B;font-size:1rem;margin-bottom:2rem;max-width:500px;margin-left:auto;margin-right:auto;line-height:1.7;}
        .btn-blue{background:#2563EB;color:#fff;padding:0.9rem 2rem;border-radius:12px;font-weight:700;font-size:1rem;text-decoration:none;border:none;cursor:pointer;transition:all 0.2s;display:inline-flex;align-items:center;gap:0.5rem;box-shadow:0 4px 16px rgba(37,99,235,0.3);}
        .btn-blue:hover{background:#1D4ED8;transform:translateY(-1px);}

        footer{background:#0F172A;color:#94A3B8;padding:3rem 2rem 2rem;border-top:1px solid #1E293B;}
        .footer-bottom-simple{max-width:1100px;margin:0 auto;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:1rem;font-size:0.82rem;}
        .footer-bottom-simple a{color:#64748B;text-decoration:none;margin-left:1.5rem;}
        .footer-bottom-simple a:hover{color:#93C5FD;}
      `}</style>

      <SharedNav mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      {/* Hero */}
      <div className="page-hero">
        <div className="page-hero-content">
          <span className="page-tag">Features</span>
          <h1 className="sora">Every tool an insurance agent needs — in one platform</h1>
          <p>From autofill to renewals, Prorately is built around how real agents actually work. No bloat, no learning curve, no excuses.</p>
          <Link to="/register" className="btn-white">🚀 Start Free Trial — No Card Needed</Link>
        </div>
      </div>

      {/* Feature cards */}
      <div className="features-grid">
        {FEATURES_DEEP.map(f => (
          <div className="feat-card" key={f.title}>
            <div className="feat-card-top">
              <div className="feat-icon-wrap" style={{ background: `${f.color}18` }}>{f.icon}</div>
              <div>
                <div className="feat-card-title">{f.title}</div>
                <div className="feat-card-tagline">{f.tagline}</div>
              </div>
            </div>
            <ul className="feat-bullets">
              {f.bullets.map(b => (
                <li key={b}>
                  <span className="feat-bullet-dot"><span className="feat-bullet-check">✓</span></span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="features-cta">
        <h2 className="sora">Ready to put these features to work?</h2>
        <p>Join 12,400+ agents already using Prorately. Your first 14 days are completely free.</p>
        <Link to="/register" className="btn-blue">Start Free Trial →</Link>
      </div>

      <footer>
        <div className="footer-bottom-simple">
          <span>© {new Date().getFullYear()} Prorately. All rights reserved.</span>
          <div>
            <a href="#">Privacy</a>
            <a href="#">Terms</a>
            <a href="#">Security</a>
          </div>
        </div>
      </footer>
    </div>
  );
}